import { useState, useEffect } from "react";
import {
  ComposedChart,
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { apiClient } from "../../../apiclient/apiclient";

const SalesTrendChart = ({ range, setRange }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const chartColor = "#2563eb";
  const primaryColor = "#172554";
  const secondaryColor = "#F69314";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await apiClient.get(
          `/sales-trend?range=${range}&metric=sales`,
        );
        setData(res.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [range]);

  return (
    <div className="bg-white p-0 flex flex-col h-full">
      <div className="flex items-center justify-between mb-6 px-0">
        <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
          Sales Trend
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#172554]"></div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Current
            </span>
          </div>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="border border-slate-200 rounded-md px-2 py-1 text-[10px] font-bold text-slate-500 uppercase outline-none focus:ring-2 focus:ring-blue-100 transition"
          >
            <option value="7days">Last 7 Days</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="h-[250px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-slate-400 text-xs font-medium italic">
              Loading sales data...
            </p>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-slate-400 text-xs font-medium italic">
              No sales data available
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <defs>
                <linearGradient
                  id="colorSalesCurrent"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#F69314" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F69314" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f1f5f9"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                axisLine={{ stroke: "#f1f5f9" }}
                tickLine={false}
                dy={10}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "#94a3b8", fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="bg-[#172554] text-white rounded-lg px-4 py-3 shadow-2xl border border-white/10">
                      <p className="text-[10px] font-bold opacity-50 uppercase tracking-widest mb-1">
                        {label}
                      </p>
                      <p className="text-base font-black italic">
                        ₹{payload[0].value.toLocaleString()}
                      </p>
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="sales"
                stroke="none"
                fillOpacity={1}
                fill="url(#colorSalesCurrent)"
              />
              <Line
                type="monotone"
                dataKey="sales"
                stroke="#172554"
                strokeWidth={3}
                dot={{
                  r: 4,
                  fill: "#172554",
                  strokeWidth: 2,
                  stroke: "#fff",
                }}
                activeDot={{ r: 6, strokeWidth: 0 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SalesTrendChart;
